import { PropsWithChildren } from 'react'
import { useCurrentUser } from 'shared/data'
import { AuthContextValue, useAuthContext } from './AuthContext'
import { LoadingStatus } from 'shared/types'
import StartupPageLoader from './StartupPageLoader'
import {
  CONTINUE_URL_QUERY_PARAM,
  EMAIL_FOR_SIGN_IN_STORAGE_KEY,
  REAUTHENTICATE_QUERY_PARAM,
  SECURITY_QUERY_PARAM,
} from 'config'
import { continueUrlCheck } from 'modules/home/pages/Login/Login.utils'
import { getQueryParamNative } from 'shared/hooks/use-query-params'
import { useSystemAdminModeTwoFactorGuard } from 'shared/hooks/use-system-admin-mode-two-factor-guard'
import {
  getReauthenticationLinkPath,
  getRevertSecondFactorLinkPath,
} from 'shared/utils'

interface AuthLoaderProps {
  user: AuthContextValue
}

let firstLoad: boolean = true

export const useAuthLoader = () => {
  const { loading: cloudUserLoading } = useCurrentUser()
  const { loading: loadingStatus } = useAuthContext()
  const firebaseUserLoading = [
    LoadingStatus.Idle,
    LoadingStatus.Pending,
  ].includes(loadingStatus)

  const loading = cloudUserLoading || firebaseUserLoading
  if (!loading && firstLoad) firstLoad = false

  return {
    loading,
    firstLoad,
  }
}

const AuthLoader = ({ children }: PropsWithChildren<AuthLoaderProps>) => {
  const { loading, firstLoad } = useAuthLoader()
  const { user, auth } = useAuthContext()
  useSystemAdminModeTwoFactorGuard()

  const continueUrl = getQueryParamNative(CONTINUE_URL_QUERY_PARAM)

  if (user && auth?.isSignInWithEmailLink(window.location.href)) {
    let shouldClean = false
    if (continueUrl) {
      try {
        const cu = new URL(continueUrl)
        const pathIsRoot = cu.pathname === '/' || cu.pathname === ''
        const isReauthFlow =
          cu.pathname.includes('sign-in-with-link') ||
          cu.searchParams.get(REAUTHENTICATE_QUERY_PARAM) === 'true' ||
          cu.searchParams.get(SECURITY_QUERY_PARAM) === 'true'
        shouldClean = pathIsRoot && !isReauthFlow
      } catch {
        shouldClean = false
      }
    }
    if (shouldClean) {
      window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_STORAGE_KEY)
      window.history.replaceState({}, '', '/')
    }
  }

  // The "second factor added" email links to the app root. Sending it on before
  // the router reads the URL saves every page shown at the root from handling it.
  const revertSecondFactorPath = getRevertSecondFactorLinkPath(window.location)
  if (revertSecondFactorPath) {
    window.history.replaceState({}, '', revertSecondFactorPath)
  }

  // Likewise for a link from the reauthentication dialog, which the start page
  // would otherwise take for a sign-in
  const reauthenticationLinkPath = getReauthenticationLinkPath(window.location)
  if (reauthenticationLinkPath) {
    window.history.replaceState({}, '', reauthenticationLinkPath)
  }

  if (continueUrl) {
    const newPath = continueUrlCheck(continueUrl)
    if (newPath) {
      window.location.replace(newPath)
      return null
    }
  }

  if (firstLoad && loading) {
    return <StartupPageLoader progressColor="secondary" fullHeight={true} />
  }

  return <>{children}</>
}
export default AuthLoader
